<script>
import DatePicker from 'vue2-datepicker'
import 'vue2-datepicker/index.css'

export default {
  name: 'McDatePicker',
  components: { DatePicker },
  inheritAttrs: false,
  props: {
    value: {
      type: [String, Number, Date, Array],
      default: null,
    },
    shortcuts: {
      type: Array,
      default: () => [],
    },
    instant: {
      type: Boolean,
      default: false,
    },
    draft: {
      type: Boolean,
      default: false,
    },
    confirm: {
      type: Boolean,
      default: false,
    },
    normalizeRangeEnd: {
      type: Function,
      default: null,
    },
  },
  data() {
    return {
      draftValue: null,
      pendingStartDate: null,
    }
  },
  computed: {
    datePickerListeners() {
      const listeners = { ...this.$listeners }
      delete listeners.input
      return listeners
    },
  },
  methods: {
    handleInput(value) {
      this.$emit('input', value)
    },
    selectShortcut(shortcut) {
      const value = shortcut.onClick(this.$refs.datePicker)
      if (!value) return

      if (this.draft) {
        this.draftValue = value
        this.pendingStartDate = null
        this.$refs.datePicker.currentValue = value
        return
      }

      // Use vue2-datepicker conversion so value-type stays consistent.
      this.$refs.datePicker.emitValue(value)
    },
    handleOpen() {
      this.draftValue = this.$refs.datePicker.innerValue
      this.pendingStartDate = null
    },
    handleClose() {
      this.draftValue = null
      this.pendingStartDate = null
    },
    handlePick(date) {
      if (!this.draft || !(date instanceof Date)) return

      if (!this.pendingStartDate) {
        this.pendingStartDate = date
        this.draftValue = [date, null]
        return
      }

      const draftValue = [this.pendingStartDate, date].sort(
        (first, second) => first.getTime() - second.getTime()
      )
      if (this.normalizeRangeEnd) {
        draftValue[1] = this.normalizeRangeEnd(draftValue[1])
      }
      this.draftValue = draftValue.map((value) => new Date(value))
      this.$nextTick(() => {
        this.$refs.datePicker.currentValue = draftValue.map((value) => new Date(value))
      })
      this.pendingStartDate = null
    },
    cancel() {
      this.$refs.datePicker.closePopup()
    },
    closePopup() {
      this.$refs.datePicker.closePopup()
    },
    openPopup(event) {
      this.$refs.datePicker.openPopup(event)
    },
    draftInputText(fallback) {
      if (!this.draft || !Array.isArray(this.draftValue)) return fallback

      const draftValue = this.draftValue.map((value) =>
        value instanceof Date ? new Date(value) : value
      )
      if (this.normalizeRangeEnd && draftValue[1] instanceof Date) {
        draftValue[1] = this.normalizeRangeEnd(draftValue[1])
      }

      return draftValue
        .filter((date) => date instanceof Date && !Number.isNaN(date.getTime()))
        .map((date) => this.$refs.datePicker.formatDate(date))
        .join(this.$attrs.rangeSeparator || ' - ')
    },
    normalizeDate(value) {
      const date = value instanceof Date ? value : new Date(value)
      if (Number.isNaN(date.getTime())) return null

      return `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`
    },
    isShortcutActive(shortcut) {
      const selectedValue = this.draft && this.draftValue ? this.draftValue : this.value
      if (!Array.isArray(selectedValue) || selectedValue.length !== 2 || !shortcut.value) return false

      const shortcutValue = typeof shortcut.value === 'function' ? shortcut.value() : shortcut.value
      if (!Array.isArray(shortcutValue) || shortcutValue.length !== 2) return false

      return shortcutValue.every(
        (date, index) => this.normalizeDate(date) === this.normalizeDate(selectedValue[index])
      )
    },
  },
}
</script>

<template>
  <date-picker
    ref="datePicker"
    v-bind="$attrs"
    :value="value"
    :shortcuts="instant || draft ? [] : shortcuts"
    :confirm="instant ? false : confirm"
    v-on="datePickerListeners"
    @input="handleInput"
    @open="handleOpen"
    @close="handleClose"
    @pick="handlePick"
  >
    <template v-if="draft" #input="{ props, events }">
      <input v-bind="props" :value="draftInputText(props.value)" v-on="events" />
    </template>

    <template v-if="(instant || draft) && shortcuts.length" #sidebar>
      <button
        v-for="(shortcut, index) in shortcuts"
        :key="index"
        type="button"
        class="mx-btn mx-btn-text mx-btn-shortcut"
        :class="{ active: isShortcutActive(shortcut) }"
        @click="selectShortcut(shortcut)"
      >
        {{ shortcut.text }}
      </button>
    </template>

    <template v-for="(_, slotName) in $slots" #[slotName]>
      <slot :name="slotName" />
    </template>
  </date-picker>
</template>
